import {attachAuthInterceptor} from '@/core/interceptors/authInterceptor'
import {attachErrorInterceptor} from '@/core/interceptors/errorInterceptor'
import {API_VERSION} from '@/utils/constants/commonConstants'
import {FeatureConfig} from '@/utils/constants/featuresFlag'
import {HTTP_METHODS} from '@/utils/constants/httpMethods'
import {toSafeQueryString} from '@/utils/security/pathSafety'
import axios from 'axios'

const TRAVERSAL_PATTERN = /(\.\.|%2e%2e|%252e%252e|%2f|%5c)/i

const sanitizeEndpointPath = endPoint => {
  const [pathPart, queryPart = ''] = String(endPoint || '').split('?')

  if (TRAVERSAL_PATTERN.test(pathPart)) {
    throw new Error(`Potentially unsafe endpoint path: ${pathPart}`)
  }

  if (!queryPart) {
    return pathPart
  }

  const sanitizedQuery = toSafeQueryString(queryPart)
  return sanitizedQuery ? `${pathPart}?${sanitizedQuery}` : pathPart
}

const normalizeQueryParams = queryParams => {
  if (!queryParams) {
    return ''
  }

  return toSafeQueryString(queryParams)
}

// Function to create axios instance dynamically
const createApiClient = (baseUrlKey, shouldAttachAuthIntercepter = true) => {
  const baseURL = import.meta.env[FeatureConfig[baseUrlKey]] || import.meta.env[FeatureConfig.inventoryApiUrl]

  const client = axios.create({baseURL, timeout: 100000})

  // Attach interceptors
  if (shouldAttachAuthIntercepter) {
    attachAuthInterceptor(client)
  }
  attachErrorInterceptor(client)

  return client
}

// Function to get mock data
const getMockData = async config => {
  const method = config?.method || HTTP_METHODS.GET
  if (method === HTTP_METHODS.GET && config?.mockData) {
    return config?.mockData
  }
  if (method === HTTP_METHODS.GET && config?.mockPath) {
    const mockData = await import(config.mockPath)
    return mockData.default || {}
  }
  return {}
}

// Main request function
export const request = async (config, options = {}, queryParams = '', scope = 'pepsi') => {
  // Return mock data immediately if available
  if (config?.mockData) {
    return config.mockData
  }

  const method = config?.method || HTTP_METHODS.GET
  let endPoint = ''
  if (config?.growthbookSdkKey) {
    endPoint = `/${config.endPoint}/${import.meta.env[FeatureConfig[config?.growthbookSdkKey]]}`
  } else if (config?.baseUrlKey) {
    endPoint = `/${config.endPoint}`
  } else {
    if (config?.overrideApiVersionPrefix) {
      endPoint = config.endPoint
    } else {
      endPoint = `${config?.version || API_VERSION.V1}/${config.endPoint}`
    }
  }

  endPoint = sanitizeEndpointPath(endPoint)

  // Handle query params – append whenever they are provided
  // This ensures that even non-multipart POST requests can carry query parameters
  const sanitizedQueryParams = normalizeQueryParams(queryParams)
  if (sanitizedQueryParams) {
    endPoint = `${endPoint}${endPoint.includes('?') ? '&' : '?'}${sanitizedQueryParams}`
  }

  const defaultOptions = {
    method,
    url: endPoint,
    headers: {'Content-Type': config?.contentType || 'application/json', ...config?.headers},
    ...options,
  }

  // Dynamically create client based on baseUrlKey or fallback to apiUrl
  const baseUrlKey = config?.baseUrlKey || 'inventoryApiUrl' // Default to 'apiUrl' if not provided
  const apiClient = createApiClient(baseUrlKey, config?.attachAuthIntercepter)
  if (options?.file) {
    const formData = new FormData()

    // append file on "file" key
    formData.append('file', options.file.file)

    // append any additional fields if provided
    if (options?.data && typeof options.data === 'object') {
      Object.entries(options.data).forEach(([key, value]) => {
        formData.append(key, value)
      })
    }

    defaultOptions.data = formData

    // Let browser set correct multipart boundary
    delete defaultOptions.headers['Content-Type']
  }

  // If formData is provided (for multipart requests), make adjustments
  if (options.formData instanceof FormData) {
    defaultOptions.data = options.formData

    defaultOptions.headers['Content-Type'] = 'multipart/form-data'
  }
  const [pathPart, existingQuery = ''] = endPoint.split('?')
  const finalParams = new URLSearchParams(existingQuery)
  if (scope) {
    finalParams.set('scope', scope)
  }
  defaultOptions.url = finalParams.toString() ? `${pathPart}?${finalParams.toString()}` : pathPart
  let response = {}
  try {
    response = await apiClient(defaultOptions)
  } catch (err) {
    const error = err?.response?.data?.message || err
    console.error('API request failed:', error)

    const isTest = import.meta.env.MODE === 'test'
    if (isTest) {
      return await getMockData(config)
    }

    // Re-throw the full error object (not just the message) so the calling
    // component can inspect `error.response.data.details`, `error.message`, etc.
    return Promise.reject(err)
  }
  return response?.data
}
